import { GamePass, isValidEntitlement, Status } from "@blacknut/javascript-sdk/dist";
import { logD } from "@blacknut/logging/dist";
import { ModalSubscription, useModal } from "@blacknut/react-sdk";
import { State } from "core";
import { useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import NotSubscribedModal from "src/components/Modals/NotSubscribedModal";
import { useSubscriptionRedirect } from "src/components/SubscriptionRedirect/SubscriptionRedirect";
import { LOGGING_TAG } from "src/utils/Utils";
import { useSignupURL } from "src/utils/useSignupURL";
import React from "react";
import { isTryBuy } from "src/utils/subscription";
import { useWarioURL } from "src/utils/useWarioURL";
import { useLocation } from "react-router-dom-v5-compat";

const useSubscribed = (passId?: string) => {
  const { push: modalPush } = useModal();
  const modalSubscription = useRef<ModalSubscription>();
  const { redirectToSubscription } = useSubscriptionRedirect();
  const { user, subscription, organization } = useSelector(
    (state: State) => state.globalState,
  );
  const subStatus = subscription.mainEntitlement?.subscription?.status;
  const isInvalidStatus =
    subStatus === Status.PAST_DUE ||
    subStatus === Status.SUSPENDED ||
    subStatus === Status.INCOMPLETE;
  const isSubscriptionValid = !!(
    (user && isValidEntitlement(subscription.mainEntitlement)) ||
    subStatus === Status.GRACE_PERIOD
  );
  const { profile } = useSelector((state: State) => state.profilesState);
  const _isTryBuy = isTryBuy(subscription.mainEntitlement);
  const { createSubURL } = useSignupURL({
    ...(passId && { uuid: passId, bundle: `${!isSubscriptionValid || _isTryBuy}` }),
  });
  const { create } = useWarioURL();
  const location = useLocation();

  const getWarioURL = useCallback(async () => {
    const warioUrl = await create(organization?.subscriptionUrl, undefined, {
      tokens: profile?.isMaster || false,
    });
    return warioUrl.toString();
  }, [create, organization?.subscriptionUrl, profile?.isMaster]);

  const getSubURL = useCallback(async () => {
    const subURL = await createSubURL();
    return subURL.toString();
  }, [createSubURL]);

  const _redirectToSubscription = useCallback(async () => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;

    if (isInvalidStatus) {
      const warioUrl = await getWarioURL();
      setTimeout(() => window.location.replace(warioUrl), 500);
    } else {
      //Le's defer redirect, otherwise, redirect will go the / but modal close will go back
      //Stream will be relaunched
      setTimeout(redirectToSubscription, 500);
    }
  }, [getWarioURL, isInvalidStatus, redirectToSubscription]);

  const _redirectToBundleOffer = useCallback(async () => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;

    //Le's defer redirect, otherwise, redirect will go the / but modal close will go back
    //Stream will be relaunched
    const subURL = await getSubURL();
    setTimeout(() => window.location.replace(subURL), 500);
  }, [getSubURL]);

  const closeModal = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
  }, []);

  const checkForValidSubscription = async (gameId: string, gamePass?: GamePass) => {
    const isInvalidGracePeriod = subStatus === Status.GRACE_PERIOD && gamePass;

    if (!isSubscriptionValid || (_isTryBuy && gamePass) || isInvalidGracePeriod) {
      const subURL = await getSubURL();
      logD(LOGGING_TAG, "Current subscription is not valid");
      modalSubscription.current = modalPush((props) => (
        <NotSubscribedModal
          {...props}
          isProfileMaster={profile?.isMaster || false}
          subscribeURL={gamePass && !isInvalidStatus ? subURL : undefined}
          onSubscribe={
            gamePass && !isInvalidStatus
              ? _redirectToBundleOffer
              : _redirectToSubscription
          }
          onClose={closeModal}
          onPlanSubscribed={closeModal}
          gamePass={gamePass}
          redirectUrl={location.pathname}
          gameId={gameId}
        />
      ));
    }

    return isSubscriptionValid && !isInvalidGracePeriod;
  };

  const checkForTrialSubscription = () => {
    if (_isTryBuy) {
      logD(LOGGING_TAG, "Current subscription is trialing");
      modalSubscription.current = modalPush((props) => (
        <NotSubscribedModal
          {...props}
          isProfileMaster={profile?.isMaster || false}
          onSubscribe={_redirectToSubscription}
          onClose={closeModal}
          onPlanSubscribed={closeModal}
          redirectUrl={location.pathname}
          gameId={""}
        />
      ));
    }

    return !_isTryBuy;
  };

  return { checkForValidSubscription, checkForTrialSubscription };
};

export default useSubscribed;
